import $ from 'jquery';
import React from 'react';
import cx from 'classnames';
import renderIf from 'render-if';
import { debounce, kebabCase } from 'lodash';
import moment from 'moment';
import ResizeScrollWatcher from '../resize_scroll_watcher/';
import {
  DeprecatedScaleManager,
  DeprecatedScaleRow,
  PresetNavigationBar,
} from 'components/common/ScaleBar';
import TimelineRows from '../timeline_rows';
import TimelineSidebar from '../timeline_sidebar';
import TimelineSidebarHeader from '../timeline_sidebar_header';
import { scrollBarSize } from 'components/common/size_helpers';
import { dateFormat } from 'javascripts/util/dateFns';
import WithScrollZoom from 'components/common/ScaleBar/withScrollZoom';
import MilestoneRow from '../milestones/milestone_row';

import {
  topOffset,
  getScaleHeight,
  getScaleBarHeight,
  contentWidth,
  getSideBarWidth,
  getScrollbarWidth,
} from '../../helpers/size_helpers';
import { updateDates } from './utils';

const debouncedUpdateDates = debounce(updateDates, 500);
const milestoneBarHeight = 40;

export default class TimelineChart extends React.Component {
  scrollerRef = React.createRef();
  fakeScrollerRef = React.createRef();
  wrapperRef = React.createRef();

  constructor(props) {
    super(props);
    // Initialize state
    const { fontSize } = props.timelineConfig;

    this.headerColumnWidth = (fontSize - 12) * 12 + 149; // Timeline-column-width calculation

    const widths =
      this.props.timelineConfig.headerWidths.length > 0
        ? this.props.timelineConfig.headerWidths
        : this.props.timelineConfig.headingTitles.map(
            () => this.headerColumnWidth
          );

    // If you remove a column, make the column width array smaller
    if (this.props.resourceTree.depth() < widths.length) {
      widths.length = this.props.resourceTree.depth();
    }
    // If you add a column, make the column width array larger
    else if (this.props.resourceTree.depth() > widths.length) {
      widths.push(
        ...Array(this.props.resourceTree.depth() - widths.length).fill(
          this.headerColumnWidth
        )
      );
    }

    this.state = {
      milestoneBarIndex: null,
      headerWidths: widths,
      persistHeaderWidths: widths,
    };
  }

  componentDidMount() {
    if (this.fakeScrollerRef.current) {
      this.fakeScrollerRef.current.scrollLeft = this.scrollLeft();
    }
  }

  getHeaderColumnsWidth = () => {
    return (
      this.headerColumnWidth * this.state.headerWidths.length -
      this.state.headerWidths.reduce((a, b) => a + b, 0)
    );
  };

  getScale() {
    const {
      toDate,
      fromDate,
      minDate,
      maxDate,
      automaticMinDate,
      automaticMaxDate,
    } = this.props.dateRange;

    return new DeprecatedScaleManager({
      minDate: minDate || automaticMinDate,
      maxDate: maxDate || automaticMaxDate,
      fromDate: fromDate,
      toDate: toDate,
      width: this.getScaleWidth(),
      fiscalMonth: this.props.timelineConfig.fiscalMonth,
      fiscalYearFormat: this.props.timelineConfig.fiscalYearFormat,
      hideWeekends: false,
      timeScale: this.props.dateRange.timeScale,
      fontSize: this.props.timelineConfig.fontSize,
    });
  }

  getScaleWidth() {
    const barSize = this.verticalScrollbarShown() ? scrollBarSize() : 0;
    const settingsBarSize = 45;

    let container = $('body');
    if (this.props.timelineConfig.dashboard) {
      container = this.wrapperRef.current;
    }
    if ($('.custom-pivot--published').length > 0) {
      container = $('.custom-pivot--published');
    }

    return (
      contentWidth(
        this.props.resourceTree,
        this.props.timelineConfig.fontSize,
        container
      ) -
      barSize -
      (this.props.timelineConfig.dashboard || window.isPublishing
        ? 0
        : settingsBarSize) +
      this.getHeaderColumnsWidth()
    );
  }

  verticalScrollbarShown() {
    if (this.props.timelineConfig.viewOnly) return false;

    const viewableArea =
      $(window).height() - (70 + $('.pivot-table-timeline').offset().top);
    return this.heightOfRows() >= viewableArea;
  }

  heightOfRows() {
    const { minDate, maxDate } = this.props.dateRange;

    return this.props.resourceTree.heightOfRows(
      this.props.timelineConfig.compactView,
      minDate,
      maxDate,
      this.props.timelineConfig.thickBars,
      this.props.timelineConfig.fontSize
    );
  }

  onSetStartDate = startDate => {
    const fromDate = moment.utc(startDate);
    this.props.changeMinDate(fromDate);
    this.props.setDates({ fromDate, toDate: this.props.dateRange.toDate });
  };

  onSetEndDate = endDate => {
    const toDate = moment.utc(endDate);
    this.props.changeMaxDate(toDate);
    this.props.setDates({ fromDate: this.props.dateRange.fromDate, toDate });
  };

  onSetVisibleDates = (startDate, endDate) => {
    const fromDate = moment.utc(startDate);
    const toDate = moment.utc(endDate);
    this.props.setDates({ fromDate, toDate });
    this.immediatelyUpdateDates(fromDate, toDate);
  };

  calculateSideBarWidth = () => {
    return getSideBarWidth(
      this.props.resourceTree,
      this.props.timelineConfig.fontSize
    );
  };

  scaleBar() {
    const {
      timelineConfig: { viewOnly },
    } = this.props;

    if (viewOnly) return;

    const { minDate, maxDate, minFromDate, maxToDate, fromDate, toDate } =
      this.props.dateRange;
    const scaleBarHeight = getScaleBarHeight(viewOnly);

    const contentWidth = this.getScaleWidth();

    const scaleBarStyle = {
      height: scaleBarHeight,
      width: contentWidth,
      left: this.calculateSideBarWidth() - 1 - this.getHeaderColumnsWidth(),
      paddingTop: '10px',
      zIndex: 4,
    };

    const presetProps = {
      maxEndDate: dateFormat(maxToDate),
      minStartDate: dateFormat(minFromDate),
      startDate: dateFormat(minDate),
      endDate: dateFormat(maxDate),
      visibleStartDate: dateFormat(fromDate),
      visibleEndDate: dateFormat(toDate),
      hideWeekends: this.getScale().options.hideWeekends,
      timescale: this.props.dateRange.timeScale,
      zoom: this.props.dateRange.zoom,
      onSetVisibleDates: this.onSetVisibleDates,
      onSetEndDate: this.onSetEndDate,
      onSetStartDate: this.onSetStartDate,
      onSetTimescale: this.props.changeTimeScale,
      onSetZoom: this.props.changeZoom,
    };

    return (
      <div className='scale-bar-wrapper' style={scaleBarStyle}>
        <PresetNavigationBar {...presetProps} />
      </div>
    );
  }

  resetStartEndDates = changeStart => {
    if (changeStart) {
      this.props.changeMinDate(null);
    } else {
      this.props.changeMaxDate(null);
    }
  };

  updateDates = () => {
    if (!this.scrollerRef.current && !this.props.dateRange.toDate) {
      return;
    }

    const scale = this.getScale();
    const scrollLeft = this.scrollerRef.current.scrollLeft;
    const { fromDate, toDate } = this.props.dateRange;

    debouncedUpdateDates({
      scale,
      scrollLeft,
      updateDates: this.immediatelyUpdateDates,
      setDates: this.props.setDates,
      fromDate,
      toDate,
    });
  };

  immediatelyUpdateDates = (fromDate, toDate) => {
    // Don't save dates if we are viewing, e.g. dashboard
    if (this.props.timelineConfig.viewOnly) {
      return;
    }

    this.props.updateDates(fromDate, toDate);
  };

  scrollLeft() {
    const { fromDate } = this.props.dateRange;
    const scale = this.getScale();
    const padding = scale.pixelsPerDay > 1 ? 1 : 0;

    return scale.dateToOffset(moment.utc(fromDate)) + padding;
  }

  handleResize() {
    this.forceUpdate();
  }

  groupRows(scale) {
    this.props.resourceTree.tree.forEach(resource => {
      this.groupRow(resource, scale);
    });
  }

  groupRow(resource, scale) {
    if (resource.isLastColumn && this.props.timelineConfig.compactView) {
      resource.groupRows(
        scale,
        this.props.timelineConfig.thickBars,
        this.isMilestone(),
        this.props.timelineConfig.showFloatingLabels
      );
    } else {
      (resource.children || []).forEach(child => {
        this.groupRow(child, scale);
      });
    }
  }

  isMilestone() {
    return this.props.timelineConfig.timelineType === 'milestone';
  }

  handleScrollFakeHorizontal = event => {
    const { target } = event;
    window.requestAnimationFrame(() => {
      this.scrollerRef.current.scrollLeft = target.scrollLeft;
    });
  };

  handleScrollScaleBar = left => {
    if (this.fakeScrollerRef.current) {
      $(this.fakeScrollerRef.current).scrollLeft(left);
    }
  };

  handleScrollContent = event => {
    if (
      this.fakeScrollerRef.current &&
      event.target === this.scrollerRef.current
    ) {
      this.fakeScrollerRef.current.scrollLeft = event.target.scrollLeft;
    }
  };

  render() {
    const {
      timelineConfig: { viewOnly, dashboard },
      dateRange,
    } = this.props;

    // The scale is broken down into into three heights, there is the outer
    // container (scaleHeight), the top row that contains the time scale buttons
    // (scaleBarHeight), and the row that actually contains the
    // labels (scaleLabelRowHeight).
    const scaleHeight = getScaleHeight(
      viewOnly,
      dashboard,
      this.props.timelineConfig.fontSize
    );
    const scaleBarHeight = getScaleBarHeight(viewOnly);
    const scaleLabelRowHeight = scaleHeight - scaleBarHeight;

    const scale = this.getScale();
    this.groupRows(scale);

    const wrapperClasses = cx(
      'timeline-pivot-wrapper',
      'common-component-wrapper',
      { publishing: viewOnly },
      { dashboard: dashboard }
    );

    const updateMilestoneBarIndex = index => {
      this.setState({ milestoneBarIndex: index });
    };

    const updateHeaderWidths = widths => {
      this.setState({ headerWidths: widths });
    };

    // This is a special case where we are changing the configuration of the timeline
    // but NOT from the configuration sidebar, where normally it is forced to be persisted
    // The configuration sidebar is loaded dynamically, meaning it's difficult to call the persist function
    // so we do it from here instead.
    const persistHeaderWidths = widths => {
      this.setState({ headerWidths: widths });
      fetch(this.props.timelineConfig.bookmarkPath, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': window.csrfToken(),
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          bookmarks_custom_pivot: { configuration_header_widths: widths },
        }),
      });
    };

    const sideBarWidth = this.calculateSideBarWidth();

    const scaleHeaderStyle = {
      top: viewOnly ? 0 : scaleBarHeight,
      left: sideBarWidth - 1 - this.getHeaderColumnsWidth(),
      height: scaleLabelRowHeight,
      width: `calc(100% - var(--left))`,
    };

    const showMilestoneBars =
      this.props.timelineConfig?.milestonesShowDates ||
      this.props.timelineConfig?.milestonesShowTitles;

    const top = viewOnly
      ? milestoneBarHeight / 2 + 5
      : scaleBarHeight + milestoneBarHeight / 2 + 5;

    // In the dashboard view, a only one level of the timeline is shown, so we need to adjust the top position of the milestone header
    const dashboardTopAdjustment = dashboard ? 18 : 0;

    // The height of the milestone header/row etc is dependent on the font size, we add extra height to compensate for larger font sizes
    let extraHeight;
    switch (this.props.timelineConfig.fontSize) {
      case 14:
        extraHeight = 4;
        break;
      case 16:
        extraHeight = 10;
        break;
      case 18:
      case 20:
        extraHeight = 20;
        break;
      default:
        extraHeight = 0;
        break;
    }

    // The height of the milestone row bar is dependent on the font size, we add extra height to compensate for larger font sizes
    // This is slightly different from the extraHeight above because the milestone row bar is a different element
    let milestoneRowBarHeight;
    switch (this.props.timelineConfig.fontSize) {
      case 16:
        milestoneRowBarHeight = 54;
        break;
      case 18:
      case 20:
        milestoneRowBarHeight = 62;
        break;
      default:
        milestoneRowBarHeight = 50;
        break;
    }

    const milestoneHeaderHeight = showMilestoneBars ? 80 + extraHeight : 32;

    // Need to add 1 pixel correction if the timeline is zoomed and is publishing (only for milestones)
    const leftZoomed =
      scale.fromDate.isSame(scale.minDate, 'day') &&
      scale.toDate.isSame(scale.maxDate, 'day') &&
      window.isPublishing
        ? 1
        : 0;

    // The plus 5 is to account for the -5px margin on the milestone header so that it sits on the same line as the scale header
    const milestoneHeaderStyle = {
      top:
        top +
        (this.props.timelineConfig.fontSize - 12) * 2 -
        dashboardTopAdjustment,
      left: sideBarWidth - this.getHeaderColumnsWidth() - leftZoomed,
      width: scale.width - 1,
      zIndex: 4,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      fontSize: this.props.timelineConfig.fontSize,
      height: milestoneHeaderHeight,
    };

    const innerWrapperStyle = {
      '--left': `${sideBarWidth - this.getHeaderColumnsWidth()}px`,
    };
    const { fromDate, toDate, minDate, maxDate } = dateRange;

    let sideBarHeight, rowsMaxHeight;
    if (!viewOnly) {
      const windowHeight = $(window).height();

      innerWrapperStyle.height = windowHeight - topOffset();
      sideBarHeight =
        Math.min(
          windowHeight - topOffset() - scaleHeight,
          this.heightOfRows()
        ) + 42;
      rowsMaxHeight = windowHeight - topOffset() - scaleHeight;
    }

    const outerWrapperStyle = {
      top: scaleHeight,
    };

    const scrollZoomOptions = {
      startDate: dateFormat(minDate),
      endDate: dateFormat(maxDate),
      visibleStartDate: dateFormat(fromDate),
      visibleEndDate: dateFormat(toDate),
      hideWeekends: scale.options.hideWeekends,
      setVisibleDates: this.onSetVisibleDates,
      pixelsPerDay: scale.pixelsPerDay,
    };

    const debouncedResize = debounce(() => {
      this.handleResize();
    }, 500);

    const themeColors = this.props.timelineConfig.reduce((acc, value, key) => {
      if (!/^theme.+/.test(key)) return acc;
      if (key === 'themeOptions') return acc;

      acc[`--custom-roadmap-${kebabCase(key)}`] = value;
      return acc;
    }, {});

    const contentWidth = this.getScaleWidth();
    themeColors['--content-width'] = `${contentWidth}px`;

    return (
      <>
        <ResizeScrollWatcher
          chart={this}
          viewOnly={this.props.timelineConfig.viewOnly}
          dashboard={this.props.timelineConfig.dashboard}
          scrollLeft={() => this.scrollLeft()}
          scaleBar={this.refs.scaleBar}
          updateScaleBarScroll={() => this.updateDates()}
          handleResize={debouncedResize}
        >
          <div className={wrapperClasses} style={themeColors}>
            <div
              className='timeline-pivot-inner-wrapper'
              style={innerWrapperStyle}
              ref={this.wrapperRef}
            >
              {this.scaleBar()}
              {renderIf(this.props.timelineConfig?.milestones.length > 0)(
                () => {
                  return (
                    <div
                      className={cx('timeline-pivot-scale-header', {
                        'view-only': viewOnly,
                        'milestone-diamonds-only': !showMilestoneBars,
                        'milestone-bars-included': showMilestoneBars,
                      })}
                      style={milestoneHeaderStyle}
                    >
                      <MilestoneRow
                        scale={scale}
                        timelineConfig={this.props.timelineConfig}
                        width={scale.totalPixelWidth()}
                        showMilestoneBars={showMilestoneBars}
                        milestoneBarHeight={milestoneRowBarHeight}
                        updateMilestoneBarIndex={updateMilestoneBarIndex}
                      />
                    </div>
                  );
                }
              )}
              <div
                className={cx('timeline-pivot-scale-header', {
                  'view-only': viewOnly,
                })}
                style={scaleHeaderStyle}
              >
                <DeprecatedScaleRow
                  scale={this.getScale()}
                  viewOnly={viewOnly}
                  scrollBarSize={0}
                  dashboard={dashboard}
                  noMinorInterval={dashboard}
                  fontSize={this.props.timelineConfig.fontSize}
                  canShowMediumInterval
                />
              </div>
              <TimelineSidebarHeader
                timelineConfig={this.props.timelineConfig}
                width={
                  this.state.headerWidths.reduce((a, b) => a + b, 0) +
                  this.state.headerWidths.length
                }
                height={scaleHeight}
                showMilestoneBars={showMilestoneBars}
                extraHeight={extraHeight}
                updateHeaderWidths={updateHeaderWidths}
                persistHeaderWidths={persistHeaderWidths}
                headerWidths={this.state.headerWidths}
              />
              <div
                className='timeline-content-scroll-wrapper-shadow'
                style={{
                  '--top': `${scaleHeight}px`,
                }}
              >
                <div
                  className='timeline-content-outer-wrapper'
                  style={outerWrapperStyle}
                  onScroll={!dashboard ? this.handleScrollContent : undefined}
                >
                  <div className='timeline-content-inner-wrapper'>
                    <TimelineSidebar
                      resourceTree={this.props.resourceTree}
                      timelineConfig={this.props.timelineConfig}
                      heightOfRows={this.heightOfRows()}
                      height={sideBarHeight}
                      scale={scale}
                      scaleWidth={contentWidth}
                      width={sideBarWidth}
                      showMilestoneBars={showMilestoneBars}
                      extraHeight={extraHeight}
                      headerWidths={this.state.headerWidths}
                      updateHeaderWidths={updateHeaderWidths}
                      getHeaderColumnsWidth={this.getHeaderColumnsWidth()}
                    />
                    <WithScrollZoom
                      {...scrollZoomOptions}
                      render={({ zoomRef }) => (
                        <TimelineRows
                          scrollerRef={this.scrollerRef}
                          wrapperRef={this.wrapperRef}
                          zoomRef={zoomRef}
                          showMilestoneBars={showMilestoneBars}
                          timelineConfig={this.props.timelineConfig}
                          resourceTree={this.props.resourceTree}
                          scale={scale}
                          scaleWidth={contentWidth}
                          maxHeight={rowsMaxHeight}
                          scrollLeft={this.scrollLeft()}
                          dependencyLinks={this.props.dependencyLinks}
                          extraHeight={extraHeight}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              {renderIf(this.verticalScrollbarShown())(
                <div
                  ref={this.fakeScrollerRef}
                  className='timeline-content-fake-horizontal-scroller'
                  style={{
                    left: sideBarWidth,
                    right: getScrollbarWidth(),
                  }}
                  onScroll={this.handleScrollFakeHorizontal}
                >
                  <div style={{ width: scale.totalPixelWidth() }} />
                </div>
              )}
            </div>
          </div>
        </ResizeScrollWatcher>
      </>
    );
  }
}
